import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { toastr } from '~/helpers/Toastr';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';
import * as DiscontinuedAlertsSlice from './DiscontinuedAlertSlice';
import { selectAlertsById } from './DiscontinuedAlertSlice';
import * as SuppliersSlice from './SuppliersSlice';
import type { IProduct } from './schema';

export const PAGE_SIZE = 50;

export const fetchAlerts = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/fetchAlerts', async (_, { dispatch, getState }) => {
	const state = getState();

	const params = {
		supplierId: state.alertsPage.selectedSupplierId,
		filterType: state.alertsPage.filterType,
		filterValue: state.alertsPage.filterValue,
		sortType: state.alertsPage.sortType,
		sortOrder: state.alertsPage.sortOrder,
		pageIndex: state.alertsPage.pageIndex,
		pageSize: PAGE_SIZE,
	};
	await dispatch(DiscontinuedAlertsSlice.fetchDiscontinuedAlerts(params));
	dispatch(fetchAlertCurrentCoverages());
});

export const fetchAlertCurrentCoverages = createAsyncThunk<
	Hub.CalculateCurrentMetaProductCoveragesResult,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'alertsPage/fetchAlertCurrentCoverages',
	async (_, { getState, rejectWithValue }) => {
		const state = getState();

		const alerts = selectAlertsById(
			state,
			state.alertsPage.currentPageAlertIds
		);

		const metaProductIds = uniq(alerts.map((x) => x.metaProductId));

		if (metaProductIds.length === 0) {
			return {
				metaProductCoverages: [],
				errors: [],
			};
		}

		try {
			const response =
				await Hub.MetaProductControllerV4.CalculateCurrentMetaProductCoverages({
					metaProductIds,
				});

			if (response.ok) {
				return response.value;
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onPageLoad = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/onPageLoad', async (_, { dispatch }) => {
	dispatch(alertsPageSlice.actions.setShowDismissModal(false));
	dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
	dispatch(fetchAlerts());
});

export const onPageChange = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/onPageChange', async (newPage: number, { dispatch }) => {
	dispatch(alertsPageSlice.actions.onPageChange(newPage));
	await dispatch(fetchAlerts());
});

export const selectSupplierFilter = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'alertsPage/selectSupplierFilter',
	async (newSupplier: number, { dispatch }) => {
		dispatch(alertsPageSlice.actions.selectSupplierFilter(newSupplier));
		await dispatch(fetchAlerts());
	}
);

export const selectFilter = createAsyncThunk<
	void,
	string,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/selectFilter', async (newFilter: string, { dispatch }) => {
	dispatch(alertsPageSlice.actions.selectFilter(newFilter));
	await dispatch(fetchAlerts());
});

export const onSortChanged = createAsyncThunk<
	void,
	{ sortType: string; sortOrder: 'ascending' | 'descending' },
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'alertsPage/onSortChanged',
	async (
		newSort: { sortType: string; sortOrder: 'ascending' | 'descending' },
		{ dispatch }
	) => {
		dispatch(alertsPageSlice.actions.onSortChanged(newSort));
		await dispatch(onPageChange(0));
	}
);

export const dismissAlertsByFilter = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'alertsPage/dismissAlertsByFilter',
	async (_, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState();
			const supplierId = state.alertsPage.selectedSupplierId ?? 0;
			const filterType = state.alertsPage.filterType ?? '';
			const filterValue = DiscontinuedAlertsSlice.convertFilterValue(
				state.alertsPage.filterValue,
				state.alertsPage.filterType
			);
			await unwrapFetch2(
				Hub.DiscontinuedAlertsController.FilteredDelete(
					supplierId,
					filterType,
					filterValue
				)
			);
			await dispatch(fetchAlerts());
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const dismissAlerts = createAsyncThunk<
	void,
	number[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'alertsPage/dismissAlerts',
	async (alertIds: number[], { dispatch, rejectWithValue }) => {
		try {
			const params = { alertIds };
			await unwrapFetch2(Hub.DiscontinuedAlertsController.BulkDelete(params));
			await dispatch(fetchAlerts());
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const dismissOneAlert = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/dismissOneAlert', async (alertId: number, { dispatch }) => {
	await dispatch(dismissAlerts([alertId]));
});

export const dismissSelectedAlerts = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('alertsPage/dismissSelectedAlerts', async (_, { dispatch, getState }) => {
	await dispatch(dismissAlerts(getState().alertsPage.selectedAlertIds));
});

export const requestLocationLevelPricingReport = createAsyncThunk<
	void,
	{ corpSupplierCode: string; productNumber: string; um: string },
	{ state: IAppState }
>(
	'metaProductPage/requestLocationLevelPricingReport',
	async (
		args: { corpSupplierCode: string; productNumber: string; um: string },
		{ getState }
	) => {
		try {
			const state = getState();
			const supplier =
				state.suppliers.customerSuppliersByCode[args.corpSupplierCode];
			const params: Hub.RequestLocationLevelPricingReportArgs = {
				supplierId: supplier?.id,
				supplierProductNumber: args.productNumber,
				unitOfMeasure: args.um,
			};
			const result = await unwrapFetch2(
				Hub.ReportController.RequestLocationLevelPricingReport(params)
			);
			if (result.errors.length == 0) {
				reportSuccessfullyRequested();
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err) {
			if (err.message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
			}
		}
	}
);

const unknownErrorRequestingReport = () => {
	toastr.error({
		title: 'Report Request Failed',
		body: 'The report was not successfully requested.',
	});
};
const reportCurrentlyInProgress = () => {
	toastr.warning({
		title: 'Report Request Failed',
		body: 'Another report request is currently in progress',
	});
};
const reportSuccessfullyRequested = () => {
	toastr.success({
		title: 'Report Request Sent',
		body: 'The report was successfully requested.',
	});
};

export interface IAlertsPageState {
	filterType: 'isBoosted' | 'wasCoverageLost' | 'metaProductId';
	filterValue?: boolean | number;
	sortType?:
		| 'discontinuedDate'
		| 'distributionCenter'
		| 'masterItemName'
		| 'description'
		| 'brand'
		| 'packSize'
		| 'wasCoverageLost'
		| 'orderedQuantity';
	sortOrder?: 'ascending' | 'descending';
	pageIndex: number;
	selectedSupplierId: number;

	alertsAreLoading: boolean;
	currentPageAlertIds: number[];
	currentCoverageIsLoading: boolean;
	currentCoverageLookup: Record<number, Hub.MetaProductCoverage>;
	displayBlueprintName: boolean;
	availableSuppliers: Hub.Supplier[];
	selectedAlertIds: number[];
	totalResults: number;
	totalPages: number;
	selectedProductKey: IProduct;
	showDismissModal: boolean;
	dismissProcessing: boolean;
}

const initialPageState: IAlertsPageState = {
	filterType: undefined,
	filterValue: undefined,
	sortType: 'discontinuedDate',
	sortOrder: 'descending',
	pageIndex: 0,
	selectedSupplierId: undefined,

	alertsAreLoading: false,
	currentCoverageIsLoading: false,
	currentPageAlertIds: [],
	currentCoverageLookup: {},
	displayBlueprintName: true,
	availableSuppliers: [],
	selectedAlertIds: [],
	totalResults: 0,
	totalPages: 0,
	selectedProductKey: {
		corpSupplierCode: '',
		supplierProductNumber: '',
		unitMeasureCode: null,
	},
	showDismissModal: false,
	dismissProcessing: false,
};

export const alertsPageSlice = createSlice({
	name: 'AlertsPage',
	initialState: initialPageState,
	reducers: {
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
		selectSupplierFilter(state, action) {
			const supplierId = action.payload;
			state.selectedSupplierId = supplierId;
		},
		selectFilter(state, action) {
			state.filterType = action.payload;
		},
		onSortChanged(state, action) {
			state.sortType = action.payload.sortType;
			state.sortOrder = action.payload.sortOrder;
		},
		toggleDisplayBlueprintName(state) {
			state.displayBlueprintName = !state.displayBlueprintName;
		},
		setSelectedAlerts(state, action) {
			state.selectedAlertIds = action.payload;
		},
		toggleSelectAlert(state, action) {
			const alertIndex = state.selectedAlertIds.indexOf(action.payload);
			const newSelectedArray = [...state.selectedAlertIds];
			if (alertIndex > -1) {
				newSelectedArray.splice(alertIndex, 1);
			} else {
				newSelectedArray.push(action.payload);
			}
			state.selectedAlertIds = newSelectedArray;
		},
		onPageChange(state, action) {
			state.pageIndex = action.payload;
		},
		setShowDismissModal(state, action) {
			state.showDismissModal = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onPageLoad.pending, (state) => {
			state.selectedAlertIds = [];
			state.showDismissModal = false;
		});
		builder.addCase(
			DiscontinuedAlertsSlice.fetchDiscontinuedAlerts.pending,
			(state) => {
				state.alertsAreLoading = true;
			}
		);
		builder.addCase(
			DiscontinuedAlertsSlice.fetchDiscontinuedAlerts.fulfilled,
			(state, action) => {
				state.totalResults = action.payload.totalResults;
				state.totalPages = action.payload.totalPages;
				state.pageIndex = action.payload.pageIndex;
				state.alertsAreLoading = false;
				state.currentPageAlertIds = action.payload.results.map(
					(alert) => alert.id
				);
			}
		);
		builder.addCase(
			DiscontinuedAlertsSlice.fetchDiscontinuedAlerts.rejected,
			(state) => {
				state.alertsAreLoading = false;
			}
		);
		builder.addCase(fetchAlertCurrentCoverages.pending, (state) => {
			state.currentCoverageIsLoading = true;
		});
		builder.addCase(fetchAlertCurrentCoverages.fulfilled, (state, action) => {
			action.payload.metaProductCoverages.forEach((metaProductCoverage) => {
				state.currentCoverageLookup[metaProductCoverage.metaProductId] =
					metaProductCoverage;
			});

			state.currentCoverageIsLoading = false;
		});
		builder.addCase(fetchAlertCurrentCoverages.rejected, (state) => {
			state.currentCoverageIsLoading = false;
		});
		builder.addCase(
			SuppliersSlice.fetchCurrentCustomerSuppliers.fulfilled,
			(state, action) => {
				state.availableSuppliers = Object.values(action.payload);
			}
		);
		builder.addCase(dismissAlertsByFilter.pending, (state) => {
			state.dismissProcessing = true;
		});
		builder.addCase(dismissAlertsByFilter.fulfilled, (state) => {
			state.dismissProcessing = false;
			state.showDismissModal = false;
		});
		builder.addCase(dismissAlerts.pending, (state) => {
			state.dismissProcessing = true;
		});
		builder.addCase(dismissAlerts.fulfilled, (state) => {
			state.dismissProcessing = false;
			state.showDismissModal = false;
			state.selectedAlertIds = initialPageState.selectedAlertIds;
		});
		builder.addCase(dismissAlerts.rejected, (state) => {
			state.dismissProcessing = false;
		});

		// NOTE: Even though it is exported, you cannot access the typesafe version of this action from the customer duck.
		builder.addCase('@ca:LOADED_CUSTOMERS_PAGE_SUCCESS', (state) => {
			state.availableSuppliers = initialPageState.availableSuppliers;
			state.currentPageAlertIds = initialPageState.currentPageAlertIds;
			state.filterType = initialPageState.filterType;
			state.filterValue = initialPageState.filterValue;
			state.pageIndex = initialPageState.pageIndex;
			state.selectedAlertIds = initialPageState.selectedAlertIds;
			state.selectedProductKey = initialPageState.selectedProductKey;
			state.selectedSupplierId = initialPageState.selectedSupplierId;
			state.sortOrder = initialPageState.sortOrder;
			state.sortType = initialPageState.sortType;
			state.totalPages = initialPageState.totalPages;
			state.totalResults = initialPageState.totalResults;
		});
	},
});
